/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { SubmitEvent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { AxiosError } from "axios";

import axios from "../../../../../api/axios";
import { ApiResources } from "../../../../../api/resources";
import Button from "../../../../../components/Button/Button";
import InputField from "../../../../../components/InputField/InputField";
import { ALERT_STATUS } from "../../../../../context/alert/types";
import {
  EXAMPLE_EMAIL,
  ROUTES,
  useEmailValidation,
  USER_INFO_ERROR_KEYS
} from "../../../../../shared";
import useResponse from "../../../../../shared/hooks/useResponse";
import AuthContainer from "../../AuthContainer";
import EnquiryText from "../EnquiryText/EnquiryText";
import ResetSent from "../ResetSent/ResetSent";

const PasswordResetRequest = () => {
  const { handleResponse } = useResponse();

  const [email, setEmail] = useState("");

  const {
    emailIsValid,
    emailError,
    setEmailErrorKey,
    validateEmail,
    handleEmailInputChange
  } = useEmailValidation(email);
  const { t } = useTranslation();

  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const showError = emailIsValid ? "" : emailError;
  const payload = {
    email
  };

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (emailIsValid) {
      try {
        setLoading(true);
        await axios.post(ApiResources.SendResetPasswordLink, payload);
        setEmail("");
        handleRedirection();
      } catch (error) {
        const err = error as AxiosError;
        if (err) {
          handleResponse(ALERT_STATUS.Critical, err.message);
        }
      } finally {
        setLoading(false);
      }
    } else {
      !emailError && setEmailErrorKey(USER_INFO_ERROR_KEYS.InvalidEmailFormat);
    }
  };

  const handleRedirection = useCallback(() => setSent(!sent), [sent]);

  return sent ? (
    <ResetSent redirect={handleRedirection} />
  ) : (
    <AuthContainer
      formName={t("Auth##forgot your password")}
      secondaryText={t("Auth##enter email")}
    >
      <form noValidate onSubmit={handleSubmit}>
        <InputField
          data-testid="forgot-password-input-field"
          size="medium"
          id="standard-error-helper-text"
          fullWidth
          name="email"
          onBlur={validateEmail}
          errorText={showError}
          onChange={e => handleEmailInputChange(e, setEmail)}
          labelLeft={t("Auth##email")}
          placeholder={`${t("Auth##example")} ${EXAMPLE_EMAIL}`}
          css={css({
            marginBottom: "16px"
          })}
        />

        <Button
          data-testid="resend-password-button"
          fullWidth
          variant="textOnly"
          size="normal"
          color="primary"
          type="submit"
          isLoading={loading}
        >
          {t("Auth##send reset link")}
        </Button>

        <EnquiryText
          primaryText={t("Auth##remember password")}
          linkText={t("Auth##sign in")}
          component={Link}
          to={ROUTES.Home}
        />
      </form>
    </AuthContainer>
  );
};

export default PasswordResetRequest;
